/**
 * FilterPanel component for bulk download UI.
 * Contains topic search, date range pills, and category filter pills.
 *
 * Used by both the upsell modal (StatelessWorkCardsUpsellModal) and the SUTD
 * page (BulkDownloadPageWorkCards). The modal and SUTD page have different
 * mobile breakpoints (767px vs 567px); use `mobileLayout` to force the
 * mobile scroll/save-button layout via JS state rather than CSS breakpoints.
 */

import React, { useEffect } from 'react';
import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';
import { Icon } from 'js/design_system/Icon';
import { PillButton } from 'js/design_system/PillButton';
import { TextArea } from 'js/design_system/forms/TextArea';
import { useAutoResizeTextarea } from 'js/utils/hooks/useAutoResizeTextarea';

import {
  DateRangeFilter,
  CATEGORY_BUCKETS,
  FilterState,
  FilterCallbacks,
  BuildPillTrackClick,
} from './types';

export interface FilterPanelProps extends FilterState, FilterCallbacks {
  /** Buckets that are available in the current data */
  availableBuckets: Set<string>;
  /** Whether to show the "Save filters" button (mobile only). @default false */
  showSaveButton?: boolean;
  /** Callback when "Save filters" is clicked */
  onSaveFilters?: () => void;
  /**
   * Optional factory for per-pill click tracking. When provided, each
   * date-range and category PillButton receives a `trackClick` prop.
   */
  buildTrackClick?: BuildPillTrackClick;
  /**
   * When true, forces mobile-accordion layout (scrollable filter area,
   * flex: 1 fill) regardless of CSS breakpoint. This lets surfaces
   * with different mobile breakpoints control the layout via JS state.
   */
  mobileLayout?: boolean;
  /** Optional className for the container */
  className?: string;
}

export function FilterPanel({
  topicQuery,
  dateRange,
  selectedBuckets,
  availableBuckets,
  onTopicQueryChange,
  onDateRangeChange,
  onSelectAllBuckets,
  onToggleBucket,
  showSaveButton = false,
  onSaveFilters,
  buildTrackClick,
  mobileLayout = false,
  className,
}: FilterPanelProps) {
  const isAllCategoriesSelected = selectedBuckets.size === 0;

  const visibleCategories = CATEGORY_BUCKETS.filter((c) =>
    availableBuckets.has(c.key),
  );

  const { textareaRef, resize: resizeTextarea } = useAutoResizeTextarea({
    minHeight: 22,
    maxHeight: 120,
  });

  useEffect(() => {
    resizeTextarea();
  }, [topicQuery, resizeTextarea]);

  return (
    <PanelRoot
      className={
        `${className ?? ''} ${mobileLayout ? 'mobileLayout' : ''}`.trim() ||
        undefined
      }
    >
      <PanelScroll className={mobileLayout ? 'mobileLayout' : undefined}>
        <FiltersInner>
          <Section>
            <SectionTitle>
              <Body size="sm" bold as="span">
                Topics
              </Body>
            </SectionTitle>
            <TopicsTextAreaWrap>
              <TextArea
                ref={textareaRef}
                prepend={<Icon name="search" size={20} />}
                placeholder='e.g. "artificial intelligence and biology"'
                value={topicQuery}
                rows={1}
                onChange={(e) => {
                  onTopicQueryChange(e.target.value);
                  resizeTextarea();
                }}
              />
            </TopicsTextAreaWrap>
          </Section>

          <Section>
            <SectionTitle>
              <Body size="sm" bold as="span">
                Date range
              </Body>
            </SectionTitle>
            <PillsRow>
              <DateRangePill
                dateRange={dateRange}
                value="all"
                label="All"
                onDateRangeChange={onDateRangeChange}
                buildTrackClick={buildTrackClick}
              />
              <DateRangePill
                dateRange={dateRange}
                value="past_year"
                label="Past year"
                onDateRangeChange={onDateRangeChange}
                buildTrackClick={buildTrackClick}
              />
              <DateRangePill
                dateRange={dateRange}
                value="past_5_years"
                label="Past 5 years"
                onDateRangeChange={onDateRangeChange}
                buildTrackClick={buildTrackClick}
              />
              <DateRangePill
                dateRange={dateRange}
                value="past_10_years"
                label="Past 10 years"
                onDateRangeChange={onDateRangeChange}
                buildTrackClick={buildTrackClick}
              />
            </PillsRow>
          </Section>

          <Section>
            <SectionTitle>
              <Body size="sm" bold as="span">
                Categories
              </Body>
            </SectionTitle>
            <PillsRow>
              <PillButton
                size="sm"
                selected={isAllCategoriesSelected}
                icon={isAllCategoriesSelected ? 'check' : undefined}
                className={isAllCategoriesSelected ? 'selectedPill' : undefined}
                type="button"
                trackClick={buildTrackClick?.({
                  filterType: 'category',
                  filterValue: 'all',
                  action: 'select_all',
                })}
                onClick={onSelectAllBuckets}
              >
                All
              </PillButton>

              {visibleCategories.map(({ key, label }) => {
                const selected = selectedBuckets.has(key);
                const action = selected ? 'remove' : 'add';
                return (
                  <PillButton
                    key={key}
                    size="sm"
                    selected={selected}
                    icon={selected ? 'check' : 'add'}
                    className={selected ? 'selectedPill' : undefined}
                    type="button"
                    trackClick={buildTrackClick?.({
                      filterType: 'category',
                      filterValue: key,
                      action,
                    })}
                    onClick={() => onToggleBucket(key)}
                  >
                    {label}
                  </PillButton>
                );
              })}
            </PillsRow>
          </Section>
        </FiltersInner>
      </PanelScroll>

      {showSaveButton && (
        <SaveFiltersButton type="button" onClick={() => onSaveFilters?.()}>
          <Body size="sm" as="span">
            Save filters
          </Body>
        </SaveFiltersButton>
      )}
    </PanelRoot>
  );
}

function DateRangePill({
  dateRange,
  value,
  label,
  onDateRangeChange,
  buildTrackClick,
}: {
  dateRange: DateRangeFilter;
  value: DateRangeFilter;
  label: string;
  onDateRangeChange: (value: DateRangeFilter) => void;
  buildTrackClick?: BuildPillTrackClick;
}) {
  const selected = dateRange === value;
  return (
    <PillButton
      size="sm"
      selected={selected}
      icon={selected ? 'check' : undefined}
      className={selected ? 'selectedPill' : undefined}
      type="button"
      trackClick={buildTrackClick?.({
        filterType: 'date_range',
        filterValue: value,
      })}
      onClick={() => onDateRangeChange(value)}
    >
      {label}
    </PillButton>
  );
}

/**
 * Outer wrapper — on desktop it's a simple flex column; on mobile it becomes
 * a flex column that can grow to fill the accordion, with the save button
 * pinned at the bottom.
 *
 * TODO: Extract shared SCSS mixin for the duplicated mobile styles between
 * `@include breakpoint(small)` and `&.mobileLayout` blocks here and in
 * FilterToggle.tsx (~50 lines total).
 */
const PanelRoot = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex-direction: column;
  width: 100%;

  @include breakpoint(small) {
    flex: 1;
    min-height: 0;
  }

  &.mobileLayout {
    flex: 1;
    min-height: 0;
  }
`;

/**
 * On desktop: transparent pass-through (display: contents).
 * On mobile: scrollable flex area so filter sections scroll independently
 * of the pinned "Save filters" button.
 */
const PanelScroll = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: contents;

  @include breakpoint(small) {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 8px var(--spacing-xs-16, 16px) 2px;
  }

  &.mobileLayout {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 8px var(--spacing-xs-16, 16px) 2px;
  }
`;

const FiltersInner = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm-24, 24px);

  @include breakpoint(small) {
    gap: var(--spacing-xs-16, 16px);
  }

  @include breakpoint(medium) {
    gap: var(--spacing-xs-16, 16px);
  }

  @include breakpoint(medium, large) {
    animation: filtersFadeIn 0.2s ease-out;
  }

  @keyframes filtersFadeIn {
    from {
      opacity: 0;
      transform: translateX(-8px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const Section = styled.div``;

const SectionTitle = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  color: var(--text-primary, #141413);
  margin-bottom: var(--spacing-xs-8, 8px);

  @include breakpoint(medium) {
    margin-bottom: 6px;
  }
`;

const TopicsTextAreaWrap = styled.div`
  :global(.ds2-5-text-area--wrapper) {
    padding: var(--spacing-xs-8, 8px);
    border-radius: var(--corner-radius-radius-md, 8px);
    border: 1px solid var(--stroke-grey-light, #dddde2);
    gap: var(--spacing-xs-8, 8px);
    margin: 0;

    &:focus-within {
      border-color: var(--stroke-focus, #0645b1);
      box-shadow: 0 0 0 1px var(--stroke-focus, #0645b1);
    }
  }

  :global(.ds2-5-text-area--slotted) {
    font-family:
      var(--type-font-family-sans-serif, 'Roboto'), Helvetica, Arial, sans-serif;
    font-size: var(--type-body-sm-size, 14px);
    line-height: 1.5;
    font-weight: var(--type-body-sm-font-weight, 400);

    &::placeholder {
      color: var(--text-placeholder, #6d6d7d);
    }
  }
`;

const PillsRow = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs-8, 8px);

  :global(.selectedPill) {
    background: var(--focus, #cddaef);
    border-color: var(--pressed, #082f75);
  }

  @include breakpoint(medium) {
    gap: 6px;

    :global(.ds-pill-button) {
      padding-left: 10px;
      padding-right: 10px;
    }
  }
`;

const SaveFiltersButton = styled.button`
  display: flex;
  flex-shrink: 0;
  width: calc(100% - 32px);
  margin: var(--spacing-sm-24, 24px) var(--spacing-xs-16, 16px)
    var(--spacing-xs-16, 16px);
  height: 32px;
  border-radius: var(--corner-radius-radius-md, 8px);
  border: 1px solid var(--text-primary, #141413);
  background: var(--background-white, #ffffff);
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

export default FilterPanel;
